import { BadgeNumber, Icon, IconDictionary } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { useCountedIssues } from '@feature/issues/use_counted_issues';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { useUser } from '@application/authentication/use_user';
import { withTranslation } from '@application/i18n/with_translation';
import { UniversalConfig } from '@application/universal_config.service';
import { useResolve } from '@visiba/instantiation';
import { UnitService } from '@application/unit.service';

const StyledNavRoot = styled.nav`
	display: flex;

	${tokens.breakpointQuery.large} {
		flex-direction: column;
		gap: ${tokens.spacing.x2};
	}

	&[data-variant='shell-3'] {
		${tokens.breakpointQuery.large} {
			flex-direction: row;
			gap: ${tokens.spacing.x2};
		}
	}
`;

const StyledButton = styled(motion.li)`
	flex-direction: column;
	display: flex;
	justify-content: center;
	align-items: center;
	width: 50px;
	height: 50px;
	border-radius: 50%;
	padding: 0 8px;
	position: relative;

	.iconArea {
		position: relative;

		& ._icon {
			transition: scale 75ms ease-in;
		}
	}

	span {
		font: ${tokens.text.label.xSmallRegular};
		white-space: nowrap;
	}

	& .badge {
		position: absolute;
		top: -10px;
		right: -10px;
	}

	&[data-is-brand='true'] {
		color: ${tokens.color.content.mutedBrand};
	}

	&[data-is-selected='true'] {
		color: ${tokens.color.content.interactive};

		${tokens.breakpointQuery.large} {
			color: ${tokens.color.content.brand};
		}
	}

	${tokens.breakpointQuery.large} {
		height: unset;
		width: unset;
		flex-direction: row;
		justify-content: start;
		gap: ${tokens.size.x2};
		min-height: ${tokens.size.x6};
		border-radius: ${tokens.borderRadius.medium};
		transition: all 75ms ease-in;

		span {
			font: ${tokens.text.label.regular};
			display: inline-flex;
			flex-direction: column;
			align-items: center;
			justify-content: space-between;
			transition: color 150ms ease-in-out;

			/*
				To avoid layout shift, this pseudo-element is added.
			*/
			&::after {
				content: attr(data-text);
				content: attr(data-text) / '';
				height: 0;
				visibility: hidden;
				overflow: hidden;
				user-select: none;
				pointer-events: none;
				font: ${tokens.text.label.semiBold};

				@media speech {
					display: none;
				}
			}
		}

		&:hover {
			background-color: rgba(111, 111, 111, 0.07);
			webkit-backdrop-filter: blur(50px);
			backdrop-filter: blur(50px);

			& .iconArea ._icon {
				scale: 1.1;
				rotate: 2deg;
			}
		}

		&[data-is-selected='true'] {
			span {
				font: ${tokens.text.label.semiBold};
			}
		}
	}
`;

function NavItemVisits() {
	const countedIssues = useCountedIssues(['ongoing', 'completed']);

	return <NavItem to='/visits' icon='archive' label={withTranslation('Component.Nav.PatientCases')} countedIssues={countedIssues} />;
}

function NavItemHome() {
	return <NavItem to='/home' icon='house' label={withTranslation('Component.Nav.Home')} />;
}

function NavItemBrowse() {
	const universalConfig = useResolve(UniversalConfig);
	const unitService = useResolve(UnitService);

	let to: string;
	let label: string;
	if (universalConfig.recentlyVisitedReceptionId.value != null) {
		const unit = unitService.getUnitNodeInTreeByReceptionId(universalConfig.recentlyVisitedReceptionId.value);

		to = unit.routeWithoutLicenceholder;
		label = unit.name;
	} else {
		to = '/browse';
		label = withTranslation('Component.Nav.Browse');
	}

	return <NavItem to={to} icon='treeChart' label={label} />;
}

function NavItemUser() {
	const user = useUser();
	if (user != null) return <NavItem to='/settings/my-account' icon='user' label={user.commonName} />;

	return <NavItem to='/sign-in' icon='user' label={withTranslation('Common.Button.SignIn')} />;
}

interface NavItemProps {
	to: string;
	icon: keyof IconDictionary;
	label: string;
	countedIssues?: number;
	onBrand?: boolean;
}

function NavItem(props: NavItemProps) {
	const countedIssues = props.countedIssues ?? 0;
	const location = useLocation();
	const [isHovering, _] = useState(false);

	return (
		<StyledButton data-is-brand={props.onBrand} data-is-selected={location.pathname === props.to} as={Link} to={props.to}>
			<div className='iconArea'>
				<Icon type={props.icon} className='_icon' />
				{countedIssues > 0 ? (
					<div className='badge'>
						<BadgeNumber variant='brandLight' label='Notiser' value={countedIssues} size='medium' />
					</div>
				) : null}
			</div>
			<span data-text={props.label}>{props.label}</span>

			<AnimatePresence mode='popLayout' initial={false}>
				{isHovering ? (
					<motion.div
						transition={{
							type: 'spring',
							stiffness: 100,
							damping: 10,
						}}
						initial={{ opacity: 0, scale: 0.97 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.97 }}
					/>
				) : null}
			</AnimatePresence>
		</StyledButton>
	);
}

export const Nav = {
	Root: (p: React.PropsWithChildren) => <>{p.children}</>,
	Content: StyledNavRoot,
	ItemHome: NavItemHome,
	ItemBrowse: NavItemBrowse,
	ItemVisits: NavItemVisits,
	ItemUser: NavItemUser,
	Item: NavItem,
} as const;
